import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Typography,
  Switch,
  DatePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useProjectsForSelection } from '@/hooks/useProject';
import { Select } from 'antd';

const { Title, Text } = Typography;

type DemandRow = {
  id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  uom: string;
  free_qty: number;
  mr_pending_qty: number;
  so_open_qty: number;
  on_order_qty: number;
  reorder_level: number;
  suggested_qty: number;
  average_cost: number;
  is_critical: boolean;
};

async function fetchDemandRows(): Promise<DemandRow[]> {
  const res = await api.get<{ rows?: Array<Record<string, unknown>> }>(
    '/inventory/reports/demand-planning',
    {
      params: { suggestions_only: 'false', page_size: 500 },
    }
  );
  const data = res.data;
  const rows = (data?.rows ?? (Array.isArray(data) ? data : []) ?? []) as Array<
    Record<string, unknown>
  >;
  return rows.map((r) => ({
    id: String(r.id ?? r.item_id ?? ''),
    item_id: String(r.id ?? r.item_id ?? ''),
    item_code: String(r.item_code ?? ''),
    item_name: String(r.item_name ?? ''),
    uom: String(r.uom ?? ''),
    free_qty: Number(r.free_qty ?? 0),
    mr_pending_qty: Number(r.mr_pending_qty ?? 0),
    so_open_qty: Number(r.so_open_qty ?? 0),
    on_order_qty: Number(r.on_order_qty ?? 0),
    reorder_level: Number(r.reorder_level ?? 0),
    suggested_qty: Number(r.suggested_qty ?? 0),
    average_cost: Number(r.average_cost ?? 0),
    is_critical: r.is_critical === true || r.is_critical === 'Yes' || r.is_critical === 1,
  }));
}

export const DemandPlanningPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [suggestionsOnly, setSuggestionsOnly] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const { data: projects = [] } = useProjectsForSelection();

  const q = useQuery({
    queryKey: ['inventory-demand-planning'],
    queryFn: fetchDemandRows,
  });

  const rows = useMemo(() => {
    const all = q.data ?? [];
    return suggestionsOnly ? all.filter((r) => r.suggested_qty > 0) : all;
  }, [q.data, suggestionsOnly]);

  const createMut = useMutation({
    mutationFn: async (values: {
      department_id: string;
      project_id: string;
      required_date?: string;
      notes?: string;
    }) => {
      const body = {
        ...values,
        item_ids: selected.length ? selected : undefined,
      };
      const res = await api.post<{
        pr_id?: string;
        pr_number?: string;
        line_count?: number;
      }>('/inventory/demand-planning/create-pr', body);
      return res.data ?? {};
    },
    onSuccess: (data: { pr_id?: string; pr_number?: string; line_count?: number }) => {
      message.success(`Created ${data.pr_number || 'PR'} (${data.line_count || 0} lines)`);
      setOpen(false);
      setSelected([]);
      qc.invalidateQueries({ queryKey: ['inventory-demand-planning'] });
      if (data.pr_id) navigate(`/scm/procurement/purchase-requests/${data.pr_id}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Failed to create PR');
    },
  });

  const columns: ColumnsType<DemandRow> = [
    { title: 'Item', dataIndex: 'item_code', width: 120 },
    { title: 'Description', dataIndex: 'item_name', ellipsis: true },
    { title: 'Free', dataIndex: 'free_qty', width: 90, align: 'right' },
    { title: 'MR pending', dataIndex: 'mr_pending_qty', width: 100, align: 'right' },
    { title: 'SO open', dataIndex: 'so_open_qty', width: 90, align: 'right' },
    { title: 'On order', dataIndex: 'on_order_qty', width: 90, align: 'right' },
    { title: 'Reorder', dataIndex: 'reorder_level', width: 90, align: 'right' },
    {
      title: 'Suggested',
      dataIndex: 'suggested_qty',
      width: 100,
      align: 'right',
      render: (v, r) => (
        <Text strong type={r.is_critical ? 'danger' : undefined}>
          {v}
        </Text>
      ),
    },
    { title: 'UOM', dataIndex: 'uom', width: 70 },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Demand planning
          </Title>
          <Text type="secondary">
            Open MR + open SO + reorder buffer − free − on-order. Workshop/maint demand included via Material
            Requests.
          </Text>
        </div>
        <Space>
          <span>Suggestions only</span>
          <Switch checked={suggestionsOnly} onChange={setSuggestionsOnly} />
          <Button onClick={() => q.refetch()}>Refresh</Button>
          <Button
            type="primary"
            disabled={!rows.some((r) => r.suggested_qty > 0)}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({
                required_date: dayjs().add(7, 'day'),
              });
              setOpen(true);
            }}
          >
            Create draft PR{selected.length ? ` (${selected.length})` : ' (all suggested)'}
          </Button>
        </Space>
      </Space>

      <Card>
        <Table
          rowKey="item_id"
          loading={q.isLoading}
          dataSource={rows}
          columns={columns}
          size="small"
          pagination={{ pageSize: 50 }}
          rowSelection={{
            selectedRowKeys: selected,
            onChange: (keys) => setSelected(keys as string[]),
            getCheckboxProps: (r) => ({ disabled: r.suggested_qty <= 0 }),
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="Create draft purchase request"
        open={open}
        onCancel={() => setOpen(false)}
        confirmLoading={createMut.isPending}
        onOk={() => form.submit()}
        okText="Create draft PR"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) =>
            createMut.mutate({
              department_id: values.department_id,
              project_id: values.project_id,
              required_date: values.required_date
                ? dayjs(values.required_date).format('YYYY-MM-DD')
                : undefined,
              notes: values.notes,
            })
          }
        >
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item
            name="project_id"
            label="Project"
            rules={[{ required: true, message: 'Project required for Direct PR lines' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select project"
              options={(projects as any[]).map((p) => ({
                value: p.id,
                label: p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name || p.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="required_date" label="Required date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DemandPlanningPage;
